import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { MIN_ZOOM, MAX_ZOOM } from './constants';
import { drawTransition, drawSelfLoop } from './drawingHelpers';
import { getStateAtPosition, getTransitionGeometry, getTransitionAtPosition, getTransitionLabelAtPosition, getCurveHandleAtPosition } from './hitTesting';
import { computeBoundingBox, renderToOffscreenCanvas } from './offscreenRenderer';
import { useInlineEditing } from './useInlineEditing';
import { collapseRanges } from '../../utils/automataHelpers';

const AutomataCanvas = forwardRef(({
  states = [],
  transitions = [],
  selectedState,
  selectedTransition,
  isPDA = false,
  isTM = false,
  highlightedStates = [],
  activeTransitions = [],
  simulationStatus = null,
  simAcceptedStateIds = [],
  stateGlowMap = null,
  transitionGlowMap = null,
  lastTakenTransitions = [],
  onStateCreate,
  onStateSelect,
  onStateMove,
  onStateRenameInline,
  onTransitionCreate,
  onTransitionSelect,
  onTransitionCurveChange,
  onTransitionLabelInline,
  onTransitionEditRequest,
  onStateDelete,
  onTransitionDelete,
  onCanvasClick,
  onContextMenu,
  onZoomChange,
  isReadOnly = false,
}, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const { isDark } = useTheme();

  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [dragTarget, setDragTarget] = useState(null);
  const [isCreatingTransition, setIsCreatingTransition] = useState(false);
  const [transitionStart, setTransitionStart] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [isDraggingCurve, setIsDraggingCurve] = useState(false);
  const [curveTarget, setCurveTarget] = useState(null);
  const [shiftClickStart, setShiftClickStart] = useState(null);
  const interactionStartRef = useRef(null);

  // Viewport state (pan + zoom)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const zoomLevelRef = useRef(1);
  const panOffsetRef = useRef({ x: 0, y: 0 });

  // Inline editing (extracted hook)
  const { editingElement, editingRef, blinkRef, blinkTick, startEditing, confirmEditing, cancelEditing } =
    useInlineEditing({ onStateRenameInline, onTransitionLabelInline, onStateDelete, onTransitionDelete });

  // Keep refs in sync with state
  useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);

  // ==========================================================================
  // Theme colors
  // ==========================================================================
  const getColors = useCallback(() => {
    const dark = isDark();
    return {
      background: dark ? '#09090b' : '#ffffff',
      grid: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
      stateFill: dark ? '#18181b' : '#ffffff',
      stateStroke: dark ? '#a1a1aa' : '#64748b',
      stateFinalStroke: dark ? '#22c55e' : '#16a34a',
      stateSelected: dark ? '#f59e0b' : '#f59e0b',
      stateFinalSelected: dark ? '#10b981' : '#10b981',
      transitionStroke: dark ? '#a1a1aa' : '#64748b',
      transitionSelected: dark ? '#f59e0b' : '#f59e0b',
      textPrimary: dark ? '#f8fafc' : '#0f172a',
      simHighlight: dark ? '#38bdf8' : '#0ea5e9',
      simAccepted: dark ? '#4ade80' : '#22c55e',
      simRejected: dark ? '#f87171' : '#ef4444',
    };
  }, [isDark]);

  // ==========================================================================
  // Imperative methods exposed via ref
  // ==========================================================================
  useImperativeHandle(ref, () => ({
    isEditing: () => !!editingRef.current,
    cancelEditing,
    resetView: () => {
      panOffsetRef.current = { x: 0, y: 0 };
      zoomLevelRef.current = 1;
      setPanOffset({ x: 0, y: 0 });
      setZoomLevel(1);
      onZoomChange?.(1);
    },
    zoomIn: () => {
      const oldZoom = zoomLevelRef.current;
      const newZoom = Math.min(MAX_ZOOM, oldZoom * 1.2);
      if (newZoom === oldZoom) return;
      const cx = canvasRef.current?.width / 2 || 400;
      const cy = canvasRef.current?.height / 2 || 300;
      const pan = panOffsetRef.current;
      const worldX = (cx - pan.x) / oldZoom;
      const worldY = (cy - pan.y) / oldZoom;
      const newPan = { x: cx - worldX * newZoom, y: cy - worldY * newZoom };
      panOffsetRef.current = newPan;
      zoomLevelRef.current = newZoom;
      setPanOffset(newPan);
      setZoomLevel(newZoom);
      onZoomChange?.(newZoom);
    },
    zoomOut: () => {
      const oldZoom = zoomLevelRef.current;
      const newZoom = Math.max(MIN_ZOOM, oldZoom / 1.2);
      if (newZoom === oldZoom) return;
      const cx = canvasRef.current?.width / 2 || 400;
      const cy = canvasRef.current?.height / 2 || 300;
      const pan = panOffsetRef.current;
      const worldX = (cx - pan.x) / oldZoom;
      const worldY = (cy - pan.y) / oldZoom;
      const newPan = { x: cx - worldX * newZoom, y: cy - worldY * newZoom };
      panOffsetRef.current = newPan;
      zoomLevelRef.current = newZoom;
      setPanOffset(newPan);
      setZoomLevel(newZoom);
      onZoomChange?.(newZoom);
    },
    fitToScreen: () => {
      if (!states.length) return;
      const padding = 80;
      const cw = canvasRef.current?.width || 800;
      const ch = canvasRef.current?.height || 600;
      const ctx = canvasRef.current?.getContext('2d');

      const bbox = computeBoundingBox(states, transitions, ctx);
      if (!bbox) return;

      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM,
        Math.min((cw - padding * 2) / bbox.width, (ch - padding * 2) / bbox.height)
      ));
      const centerWorldX = (bbox.minX + bbox.maxX) / 2;
      const centerWorldY = (bbox.minY + bbox.maxY) / 2;
      const newPan = {
        x: cw / 2 - centerWorldX * newZoom,
        y: ch / 2 - centerWorldY * newZoom,
      };

      panOffsetRef.current = newPan;
      zoomLevelRef.current = newZoom;
      setPanOffset(newPan);
      setZoomLevel(newZoom);
      onZoomChange?.(newZoom);
    },
    copyCanvasImage: () => {
      const offCanvas = renderToOffscreenCanvas(states, transitions, getColors());
      if (!offCanvas) return Promise.reject('No states to copy');
      const blobPromise = new Promise((resolve) => {
        offCanvas.toBlob(blob => resolve(blob), 'image/png');
      });
      return navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blobPromise })
      ]);
    },
    downloadCanvasImage: (filename = 'automaton.png') => {
      const offCanvas = renderToOffscreenCanvas(states, transitions, getColors());
      if (!offCanvas) return Promise.reject('No states to download');
      return new Promise((resolve) => {
        offCanvas.toBlob(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          resolve();
        }, 'image/png');
      });
    },
    getCanvasImageBlob: () => {
      const offCanvas = renderToOffscreenCanvas(states, transitions, getColors());
      if (!offCanvas) return Promise.resolve(null);
      return new Promise((resolve) => {
        offCanvas.toBlob(blob => resolve(blob), 'image/png');
      });
    },
    getViewState: () => ({
      panOffset: { ...panOffsetRef.current },
      zoomLevel: zoomLevelRef.current,
    }),
    setViewState: ({ panOffset: pan, zoomLevel: zoom }) => {
      panOffsetRef.current = pan;
      zoomLevelRef.current = zoom;
      setPanOffset(pan);
      setZoomLevel(zoom);
      onZoomChange?.(zoom);
    },
    startEditingState: (stateId) => {
      const state = states.find(s => s.id === stateId);
      if (state) startEditing('state', state.id, state.name);
    },
    isEditing: () => !!editingRef.current,
    startEditingTransition: (transitionId) => {
      const trans = transitions.find(t => t.id === transitionId);
      if (!trans) return;
      if (isPDA || isTM) {
        const fromState = states.find(s => s.id === trans.from_state);
        const toState = states.find(s => s.id === trans.to_state);
        if (fromState && toState) {
          const z = zoomLevelRef.current;
          const p = panOffsetRef.current;
          const mx = ((fromState.x + toState.x) / 2) * z + p.x;
          const my = ((fromState.y + toState.y) / 2) * z + p.y;
          onTransitionEditRequest?.(trans.id, mx, my);
        }
      } else {
        startEditing('transition', trans.id, collapseRanges(trans.symbols));
      }
    },
  }), [onZoomChange, states, transitions, getColors, cancelEditing, startEditing, isPDA, isTM, onTransitionEditRequest]);

  // ==========================================================================
  // Coordinate conversion
  // ==========================================================================
  const screenToWorld = useCallback((sx, sy) => ({
    x: (sx - panOffset.x) / zoomLevel,
    y: (sy - panOffset.y) / zoomLevel,
  }), [panOffset, zoomLevel]);

  const worldToScreen = useCallback((wx, wy) => ({
    x: wx * zoomLevel + panOffset.x,
    y: wy * zoomLevel + panOffset.y,
  }), [panOffset, zoomLevel]);

  // ==========================================================================
  // Resize observer
  // ==========================================================================
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };
    handleResize();
    const observer = new ResizeObserver(handleResize);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // ==========================================================================
  // Wheel handler for zoom (cursor-centered, supports trackpad pinch)
  // ==========================================================================
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (isPanningRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;

    const delta = -e.deltaY;
    const zoomFactor = delta > 0 ? 1.05 : 1 / 1.05;

    const oldZoom = zoomLevelRef.current;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldZoom * zoomFactor));
    if (newZoom === oldZoom) return;

    const pan = panOffsetRef.current;
    const worldX = (sx - pan.x) / oldZoom;
    const worldY = (sy - pan.y) / oldZoom;
    const newPan = { x: sx - worldX * newZoom, y: sy - worldY * newZoom };

    panOffsetRef.current = newPan;
    zoomLevelRef.current = newZoom;
    setPanOffset(newPan);
    setZoomLevel(newZoom);
    onZoomChange?.(newZoom);
  }, [onZoomChange]);

  // Attach wheel handler with passive: false to allow preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // ==========================================================================
  // Canvas draw effect
  // ==========================================================================
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const colors = getColors();

    // Clear canvas
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = colors.grid;
    ctx.lineWidth = 1;
    const baseGridSize = 20;
    let gridSize = baseGridSize * zoomLevel;
    while (gridSize < 10) gridSize *= 2;
    while (gridSize > 60) gridSize /= 2;
    const offsetX = ((panOffset.x % gridSize) + gridSize) % gridSize;
    const offsetY = ((panOffset.y % gridSize) + gridSize) % gridSize;

    for (let x = offsetX; x < canvas.width; x += gridSize) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke();
    }
    for (let y = offsetY; y < canvas.height; y += gridSize) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke();
    }

    // Apply pan + zoom for all world-space drawing
    ctx.save();
    ctx.translate(panOffset.x, panOffset.y);
    ctx.scale(zoomLevel, zoomLevel);

    // Draw transitions
    transitions.forEach(transition => {
      const fromState = states.find(s => s.id === transition.from_state);
      const toState = states.find(s => s.id === transition.to_state);
      if (!fromState || !toState) return;

      const isSelected = selectedTransition === transition.id;
      const transGlow = transitionGlowMap
        ? (transitionGlowMap[transition.id] ?? 0)
        : (activeTransitions.includes(transition.id) ? 1 : 0);
      const isLastTaken = lastTakenTransitions.includes(transition.id);

      if (transGlow > 0.05) {
        ctx.strokeStyle = colors.simHighlight;
        ctx.lineWidth = 2 + 2 * transGlow;
        ctx.shadowColor = colors.simHighlight;
        ctx.shadowBlur = 15 * transGlow;
      } else if (isLastTaken) {
        ctx.strokeStyle = colors.simHighlight;
        ctx.lineWidth = 2;
      } else {
        ctx.strokeStyle = isSelected ? colors.transitionSelected : colors.transitionStroke;
        ctx.lineWidth = isSelected ? 3 : 2;
      }

      if (fromState.id === toState.id) {
        drawSelfLoop(ctx, fromState, transition, colors, transition.loopAngle, isSelected);
      } else {
        let effectiveCurve = transition.curve || 0;
        if (effectiveCurve === 0) {
          const hasReverse = transitions.some(t =>
            t.from_state === transition.to_state &&
            t.to_state === transition.from_state
          );
          if (hasReverse) effectiveCurve = 1.2;
        }
        drawTransition(ctx, fromState, toState, transition, colors, effectiveCurve, isSelected);
      }

      if (transGlow > 0.05) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; }
    });

    // Draw transition being created
    if (isCreatingTransition && transitionStart) {
      ctx.strokeStyle = colors.stateSelected;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(transitionStart.x, transitionStart.y);
      ctx.lineTo(mousePos.x, mousePos.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw states
    states.forEach(state => {
      const isSelected = selectedState === state.id;

      // Draw initial state arrow
      if (state.is_initial) {
        ctx.strokeStyle = colors.stateStroke;
        ctx.fillStyle = colors.stateStroke;
        ctx.lineWidth = 2;
        const arrowLen = 40;
        const arrowX = state.x - state.radius - arrowLen;
        const tipX = state.x - state.radius;
        const tipY = state.y;
        ctx.beginPath(); ctx.moveTo(arrowX, state.y); ctx.lineTo(tipX, tipY); ctx.stroke();
        const arrowHeadLen = 10;
        const arrowAngle = 0;
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(tipX - arrowHeadLen * Math.cos(arrowAngle - Math.PI / 6), tipY - arrowHeadLen * Math.sin(arrowAngle - Math.PI / 6));
        ctx.lineTo(tipX - arrowHeadLen * Math.cos(arrowAngle + Math.PI / 6), tipY - arrowHeadLen * Math.sin(arrowAngle + Math.PI / 6));
        ctx.closePath(); ctx.fill();
      }

      // Draw state circle
      const stateGlow = stateGlowMap
        ? (stateGlowMap[state.id] ?? 0)
        : (highlightedStates.includes(state.id) ? 1 : 0);
      const isHighlighted = stateGlow > 0.01;
      const simColor = isHighlighted
        ? (simulationStatus === 'accepted'
          ? (simAcceptedStateIds.includes(state.id) ? colors.simAccepted : colors.simHighlight)
          : simulationStatus === 'rejected' || simulationStatus === 'stuck'
          ? colors.simRejected
          : colors.simHighlight)
        : null;
      if (isHighlighted) { ctx.shadowColor = simColor; ctx.shadowBlur = 20 * stateGlow; }
      ctx.beginPath();
      ctx.arc(state.x, state.y, state.radius, 0, Math.PI * 2);
      ctx.fillStyle = colors.stateFill;
      ctx.fill();
      ctx.strokeStyle = isHighlighted ? simColor : isSelected ? (state.is_final ? colors.stateFinalSelected : colors.stateSelected) : (state.is_final ? colors.stateFinalStroke : colors.stateStroke);
      ctx.lineWidth = isHighlighted ? (3 + stateGlow) : isSelected ? 4 : 3;
      ctx.stroke();
      if (isHighlighted) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; }

      // Draw inner circle for final states
      if (state.is_final) {
        ctx.beginPath();
        ctx.arc(state.x, state.y, state.radius - 6, 0, Math.PI * 2);
        ctx.strokeStyle = isHighlighted ? simColor : isSelected ? colors.stateFinalSelected : colors.stateFinalStroke;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      // Draw reject state indicator (double circle with red/dashed inner ring)
      if (state.is_reject) {
        ctx.beginPath();
        ctx.arc(state.x, state.y, state.radius - 6, 0, Math.PI * 2);
        ctx.strokeStyle = isHighlighted ? simColor : '#ef4444';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Draw state name (with inline editing cursor/selection)
      const isEditingThis = editingRef.current?.type === 'state' && editingRef.current?.id === state.id;
      const displayName = isEditingThis ? editingRef.current.text : state.name;
      ctx.font = '14px JetBrains Mono';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Selection highlight
      if (isEditingThis && editingRef.current.selectionStart !== undefined && editingRef.current.selectionStart !== editingRef.current.cursorPos) {
        const fullWidth = ctx.measureText(displayName).width;
        const selStart = Math.min(editingRef.current.selectionStart, editingRef.current.cursorPos);
        const selEnd = Math.max(editingRef.current.selectionStart, editingRef.current.cursorPos);
        const beforeSel = ctx.measureText(displayName.slice(0, selStart)).width;
        const selWidth = ctx.measureText(displayName.slice(selStart, selEnd)).width;
        ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
        ctx.fillRect(state.x - fullWidth / 2 + beforeSel, state.y - 8, selWidth, 16);
      }

      ctx.fillStyle = colors.textPrimary;
      ctx.fillText(displayName, state.x, state.y);

      // Cursor for inline editing
      if (isEditingThis && blinkRef.current && (editingRef.current.selectionStart === undefined || editingRef.current.selectionStart === editingRef.current.cursorPos)) {
        const fullWidth = ctx.measureText(displayName).width;
        const beforeCursor = displayName.slice(0, editingRef.current.cursorPos);
        const beforeWidth = ctx.measureText(beforeCursor).width;
        const cursorX = state.x - fullWidth / 2 + beforeWidth;
        ctx.strokeStyle = colors.textPrimary;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(cursorX, state.y - 8); ctx.lineTo(cursorX, state.y + 8); ctx.stroke();
      }
    });

    // Draw inline editing cursor for transitions
    if (editingRef.current?.type === 'transition') {
      const editTrans = transitions.find(t => t.id === editingRef.current.id);
      if (editTrans) {
        const fromState = states.find(s => s.id === editTrans.from_state);
        const toState = states.find(s => s.id === editTrans.to_state);
        if (fromState && toState) {
          const editText = editingRef.current.text;
          ctx.font = '12px JetBrains Mono';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';

          let labelX, labelY;
          if (fromState.id === toState.id) {
            const angle = editTrans.loopAngle !== undefined ? editTrans.loopAngle : -Math.PI / 2;
            const loopHeight = 30;
            const d = fromState.radius + loopHeight - 5;
            const cx = fromState.x + Math.cos(angle) * d;
            const cy = fromState.y + Math.sin(angle) * d;
            const rotation = angle + Math.PI / 2;
            const labelGap = 10;
            const nx = Math.sin(rotation), ny = -Math.cos(rotation);
            labelX = cx + nx * (loopHeight + labelGap);
            labelY = cy + ny * (loopHeight + labelGap);
          } else {
            const dx = toState.x - fromState.x, dy = toState.y - fromState.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const perpX = -dy / dist, perpY = dx / dist;
            let curve = editTrans.curve || 0;
            if (curve === 0) {
              const hasReverse = transitions.some(r => r.from_state === editTrans.to_state && r.to_state === editTrans.from_state);
              if (hasReverse) curve = 1.2;
            }
            const curveOffset = curve * dist * 0.5;
            const midX = (fromState.x + toState.x) / 2, midY = (fromState.y + toState.y) / 2;
            const ctrlX = midX + perpX * curveOffset, ctrlY = midY + perpY * curveOffset;
            const fDx = ctrlX - fromState.x, fDy = ctrlY - fromState.y;
            const fDist = Math.sqrt(fDx * fDx + fDy * fDy);
            const startX = fromState.x + (fDx / fDist) * fromState.radius;
            const startY = fromState.y + (fDy / fDist) * fromState.radius;
            const tDx = toState.x - ctrlX, tDy = toState.y - ctrlY;
            const tDist = Math.sqrt(tDx * tDx + tDy * tDy);
            const endX = toState.x - (tDx / tDist) * toState.radius;
            const endY = toState.y - (tDy / tDist) * toState.radius;
            const arcMidX = (startX + 2 * ctrlX + endX) / 4;
            const arcMidY = (startY + 2 * ctrlY + endY) / 4;
            const sign = curveOffset >= 0 ? 1 : -1;
            const labelOffsetX = perpX * sign * 14;
            const labelOffsetY = perpY * sign * 14;
            labelX = arcMidX + labelOffsetX;
            labelY = arcMidY + labelOffsetY;
          }

          // Background for edit text
          const textWidth = Math.max(ctx.measureText(editText || ' ').width + 8, 20);
          ctx.fillStyle = colors.background;
          ctx.fillRect(labelX - textWidth / 2 - 2, labelY - 9, textWidth + 4, 18);
          ctx.strokeStyle = colors.stateSelected;
          ctx.lineWidth = 1;
          ctx.strokeRect(labelX - textWidth / 2 - 2, labelY - 9, textWidth + 4, 18);

          // Selection highlight
          if (editingRef.current.selectionStart !== undefined && editingRef.current.selectionStart !== editingRef.current.cursorPos) {
            const fullWidth = ctx.measureText(editText).width;
            const selStart = Math.min(editingRef.current.selectionStart, editingRef.current.cursorPos);
            const selEnd = Math.max(editingRef.current.selectionStart, editingRef.current.cursorPos);
            const beforeSel = ctx.measureText(editText.slice(0, selStart)).width;
            const selWidth = ctx.measureText(editText.slice(selStart, selEnd)).width;
            ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
            ctx.fillRect(labelX - fullWidth / 2 + beforeSel, labelY - 7, selWidth, 14);
          }

          // Text
          ctx.fillStyle = colors.textPrimary;
          ctx.fillText(editText, labelX, labelY);

          // Cursor
          if (blinkRef.current && (editingRef.current.selectionStart === undefined || editingRef.current.selectionStart === editingRef.current.cursorPos)) {
            const fullWidth = ctx.measureText(editText).width;
            const beforeCursor = editText.slice(0, editingRef.current.cursorPos);
            const beforeWidth = ctx.measureText(beforeCursor).width;
            const cursorX = labelX - fullWidth / 2 + beforeWidth;
            ctx.strokeStyle = colors.textPrimary;
            ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.moveTo(cursorX, labelY - 7); ctx.lineTo(cursorX, labelY + 7); ctx.stroke();
          }
        }
      }
    }

    ctx.restore();
  }, [states, transitions, selectedState, selectedTransition, highlightedStates, activeTransitions, simulationStatus, simAcceptedStateIds, stateGlowMap, transitionGlowMap, lastTakenTransitions, isCreatingTransition, transitionStart, mousePos, canvasSize, getColors, panOffset, zoomLevel, editingElement, blinkTick]);

  // ==========================================================================
  // Mouse event handlers
  // ==========================================================================
  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(sx, sy);

    // If editing, confirm first
    if (editingRef.current) {
      confirmEditing();
    }

    const state = getStateAtPosition(states, x, y);

    // Check for transition interaction when not on a state
    if (!state) {
      if (selectedTransition && !isReadOnly) {
        const handleTransition = getCurveHandleAtPosition(states, transitions, x, y);
        if (handleTransition && handleTransition.id === selectedTransition) {
          setIsDraggingCurve(true);
          setCurveTarget(handleTransition);
          interactionStartRef.current = { x, y };
          return;
        }
      }
      const transition = getTransitionAtPosition(states, transitions, x, y);
      if (transition) {
        onTransitionSelect?.(transition.id);
        if (!isReadOnly) {
          setIsDraggingCurve(true);
          setCurveTarget(transition);
          interactionStartRef.current = { x, y };
        }
        return;
      }
    }

    if (e.shiftKey && state && !isReadOnly) {
      setIsCreatingTransition(true);
      setTransitionStart({ state, x: state.x, y: state.y, startX: x, startY: y });
      setShiftClickStart({ x, y, state });
      setMousePos({ x, y });
    } else if (state) {
      onStateSelect?.(state.id);
      if (!isReadOnly) {
        setIsDragging(true);
        setDragTarget(state);
        interactionStartRef.current = { x, y };
      }
    } else {
      isPanningRef.current = true;
      setIsPanning(true);
      panStartRef.current = { x: sx, y: sy, offsetX: panOffset.x, offsetY: panOffset.y };
      onCanvasClick?.();
    }
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(sx, sy);

    if (isPanningRef.current) {
      const dx = sx - panStartRef.current.x;
      const dy = sy - panStartRef.current.y;
      setPanOffset({ x: panStartRef.current.offsetX + dx, y: panStartRef.current.offsetY + dy });
      return;
    }

    if (isDraggingCurve && curveTarget) {
      const geo = getTransitionGeometry(states, transitions, curveTarget);
      if (geo && geo.type === 'curve') {
        const dx = x - geo.midX;
        const dy = y - geo.midY;
        const projectedDist = dx * geo.perpX + dy * geo.perpY;
        const newCurve = projectedDist / (geo.dist * 0.2);
        onTransitionCurveChange?.(curveTarget.id, { curve: Math.max(-5, Math.min(5, newCurve)) });
      } else if (geo && geo.type === 'self-loop') {
        let angle = Math.atan2(y - geo.state.y, x - geo.state.x);
        if (e.shiftKey) {
          const snap = Math.PI / 4;
          angle = Math.round(angle / snap) * snap;
        }
        onTransitionCurveChange?.(curveTarget.id, { loopAngle: angle });
      }
    } else if (isCreatingTransition) {
      setMousePos({ x, y });
    } else if (isDragging && dragTarget) {
      onStateMove?.(dragTarget.id, x, y);
    }
  };

  const handleMouseUp = (e) => {
    if (isPanningRef.current) {
      isPanningRef.current = false;
      setIsPanning(false);
      return;
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(sx, sy);

    if (isDraggingCurve) {
      setIsDraggingCurve(false);
      setCurveTarget(null);
      interactionStartRef.current = null;
      return;
    }

    if (isCreatingTransition && transitionStart) {
      const endState = getStateAtPosition(states, x, y);
      if (endState) {
        let newTransitionId = null;
        if (shiftClickStart) {
          const dragDistance = Math.sqrt(
            (x - shiftClickStart.x) * (x - shiftClickStart.x) +
            (y - shiftClickStart.y) * (y - shiftClickStart.y)
          );
          if (dragDistance < 10 && endState.id === transitionStart.state.id) {
            newTransitionId = onTransitionCreate?.(transitionStart.state.id, transitionStart.state.id);
          } else {
            newTransitionId = onTransitionCreate?.(transitionStart.state.id, endState.id);
          }
        } else {
          newTransitionId = onTransitionCreate?.(transitionStart.state.id, endState.id);
        }
        if (newTransitionId) {
          if (isPDA || isTM) {
            const screenPos = worldToScreen(x, y);
            setTimeout(() => onTransitionEditRequest?.(newTransitionId, screenPos.x, screenPos.y), 50);
          } else {
            setTimeout(() => startEditing('transition', newTransitionId, ''), 50);
          }
        }
      }
      setIsCreatingTransition(false);
      setTransitionStart(null);
      setShiftClickStart(null);
    } else if (isDragging && dragTarget) {
      interactionStartRef.current = null;
    }

    setIsDragging(false);
    setDragTarget(null);
  };

  const handleDoubleClick = (e) => {
    if (isReadOnly) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(sx, sy);

    if (editingRef.current) {
      cancelEditing();
    }

    const state = getStateAtPosition(states, x, y);
    if (state) {
      // Double-click state = rename
      startEditing('state', state.id, state.name);
    } else {
      const transition = getTransitionAtPosition(states, transitions, x, y);
      if (transition) {
        // Double-click transition = edit label/rules
        if (isPDA || isTM) {
          const screenPos = worldToScreen(x, y);
          onTransitionEditRequest?.(transition.id, screenPos.x, screenPos.y);
        } else {
          startEditing('transition', transition.id, collapseRanges(transition.symbols));
        }
      } else {
        onStateCreate?.(x, y);
      }
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const { x, y } = screenToWorld(sx, sy);

    if (editingRef.current) {
      confirmEditing();
    }

    const state = getStateAtPosition(states, x, y);
    if (state) {
      onStateSelect?.(state.id);
      onContextMenu?.({ type: 'state', id: state.id, screenX: e.clientX - rect.left, screenY: e.clientY - rect.top });
      return;
    }
    const transition = getTransitionAtPosition(states, transitions, x, y);
    if (transition) {
      onTransitionSelect?.(transition.id);
      onContextMenu?.({ type: 'transition', id: transition.id, screenX: e.clientX - rect.left, screenY: e.clientY - rect.top });
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <div
      ref={containerRef}
      className="w-full h-full relative canvas-container"
      data-testid="automata-canvas-container"
    >
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        className={`absolute inset-0 ${isPanning ? 'cursor-grabbing' : isReadOnly ? 'cursor-default' : 'cursor-crosshair'}`}
        data-testid="automata-canvas"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
      />
    </div>
  );
});

export default AutomataCanvas;
